import React from "react";
import PropTypes from "prop-types";

import { Button, Drawer, Popconfirm, Skeleton, Spin } from "antd";
import { CardWrapper, PageTitleWrapper, ActionItemWrapper, DrawerFooterWrapper, PageSubTitle1Wrapper, TableWrapper, PageDropDownWrapper, WarningMessage } from "./Commons.style";

import { ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { getQuestionCountMessage } from "Helpers/QuestionLimits";

export function Card(props) {
	return <CardWrapper {...props} />;
}

export function PageTitle({ titlePosition = "center", children }) {
	return <PageTitleWrapper titlePosition={titlePosition}>{children}</PageTitleWrapper>;
}

export function PageDropDown({ customStyle = {}, children }) {
	return <PageDropDownWrapper style={customStyle}>{children}</PageDropDownWrapper>;
}

export function PageSubTitle1(props) {
	return <PageSubTitle1Wrapper>{props.children}</PageSubTitle1Wrapper>;
}

export function PageWarningMessage(props) {
	return <WarningMessage>{props.children}</WarningMessage>;
}

export function QuestionCountMessage({ count, min, max, noun = "Questions", visible = true, loading = false }) {
	if (!visible || loading) {
		return null;
	}

	return <PageWarningMessage>* {getQuestionCountMessage(count, { min, max, noun })}</PageWarningMessage>;
}

export function ActionItem(props) {
	return <ActionItemWrapper {...props}>{props.children}</ActionItemWrapper>;
}

function DeleteTooltip(props) {
	return (
		<Popconfirm title="Are you sure delete this?" onConfirm={props.onConfirm} onCancel={props.onCancel} okText="Yes" cancelText="No">
			<ActionItem>{props.children}</ActionItem>
		</Popconfirm>
	);
}

DeleteTooltip.propTypes = {
	onConfirm: PropTypes.func.isRequired,
};

/* DRAWER */
function SideDrawer(props) {
	return <Drawer placement={"right"} width={450} footer={<DrawerFooter {...props} />} {...props}></Drawer>;
}

function DrawerFooter(props) {
	return (
		<DrawerFooterWrapper>
			{props.onConfirm && (
				<Button type="primary" loading={props.loading} onClick={props.onConfirm}>
					{" "}
					{props.submitText || "Submit"}{" "}
				</Button>
			)}
			<Button onClick={props.onClose}>Close</Button>
		</DrawerFooterWrapper>
	);
}

function ReturnButton(props) {
	const history = useHistory();
	return (
		<div style={{ padding: "10px 0" }}>
			<ArrowLeftOutlined onClick={() => history.goBack()} />
		</div>
	);
}

// Table
/** How many placeholder rows to draw while the first page of data is in flight. */
const SKELETON_ROW_COUNT = 5;

/**
 * Builds a throwaway table body that mirrors the real column layout.
 *
 * While a question list loaded, the table collapsed to a one-line "Loading..."
 * box and then snapped open to full height once the rows arrived, so the page
 * jumped under the cursor (bug report module 6, issue 11). Drawing shaded rows
 * of the right shape keeps the layout still and makes the wait legible.
 */
function buildSkeleton(columns) {
	const source = Array.isArray(columns) ? columns : [];
	const skeletonColumns = source.map((column, index) => ({
		...column,
		key: column?.key || column?.dataIndex || `skeleton-col-${index}`,
		// Sorting, filtering and per-cell handlers have nothing to act on yet.
		sorter: undefined,
		filters: undefined,
		filterDropdown: undefined,
		onCell: undefined,
		render: () => <Skeleton.Input active size="small" style={{ width: "70%", minWidth: 32, height: 14 }} />,
	}));
	const rows = [];
	for (let index = 0; index < SKELETON_ROW_COUNT; index += 1) {
		rows.push({ id: `__skeleton-${index}`, __skeleton: true });
	}
	return { rows, skeletonColumns };
}

function TableContainer(props) {
	const showLoading = !!props.loading;
	const hasRows = Array.isArray(props.dataSource) && props.dataSource.length > 0;
	// Only the very first load gets the skeleton. A refresh that already has rows on
	// screen keeps them under the usual spinner overlay, which is far less jarring.
	const showSkeleton = showLoading && !hasRows && !props?.tableMessage && Array.isArray(props.columns) && props.columns.length > 0;

	if (showSkeleton) {
		const { rows, skeletonColumns } = buildSkeleton(props.columns);
		return (
			<TableWrapper
				{...props}
				dataSource={rows}
				columns={skeletonColumns}
				loading={false}
				pagination={false}
				rowKey="id"
				locale={{ emptyText: "Loading..." }}
			/>
		);
	}

	return (
		<TableWrapper
			// rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
			{...props}
			loading={showLoading}
			locale={{ emptyText: props?.tableMessage ? props.tableMessage : showLoading ? "Loading..." : "No Data Available" }}
			rowKey={props?.rowKey || "id"}
		/>
	);
}
SideDrawer.propTypes = {
	title: PropTypes.string.isRequired,
	onClose: PropTypes.func.isRequired,
	visible: PropTypes.bool.isRequired,
	submitText: PropTypes.string,
};

export function SimpleLoader() {
	return (
		<CardWrapper spacing={24}>
			<div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 280 }}>
				<Spin />
			</div>
		</CardWrapper>
	);
}

export { DeleteTooltip, SideDrawer, ReturnButton, TableContainer };
export { ModuleTableFallback } from "./ModuleTableFallback";
